import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useWorkspace } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Card,
	CheckmarkIcon,
	ChevronRightIcon,
	Count,
	ListItem,
	Subheading,
	tokens,
} from '@treams/treams-ui';

const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.element}px;
	margin-bottom: ${tokens.gap.group}px;
`;

const List = styled.div`
	[data-list-item]:last-child {
		border-bottom: none;
	}
`;

const Status = styled.div<{ $completed?: boolean }>`
	box-sizing: border-box;
	width: 24px;
	height: 24px;
	border-radius: ${tokens.radius.full}px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.small}px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: 1;

	${(props) =>
		props.$completed
			? `
		background: ${tokens.success.icon};
		color: ${tokens.text.white};
		border: none;

		svg {
			width: 14px !important;
			height: 14px !important;
			color: inherit;
		}
	`
			: `
		background: ${tokens.surface.white};
		color: ${tokens.text.secondary};
		border: 1.5px solid ${tokens.border.default};
	`}
`;

const Title = styled.span<{ $completed?: boolean }>`
	color: ${(props) => (props.$completed ? tokens.text.tertiary : 'inherit')};
`;

const Chevron = styled.span<{ $completed?: boolean }>`
	display: flex;
	color: ${(props) => (props.$completed ? tokens.icon.tertiary : tokens.icon.secondary)};

	svg {
		width: 16px;
		height: 16px;
	}
`;

function Checkpoint(props: {
	index: number;
	completed?: boolean;
	url: string;
	title: string;
	action: string;
}) {
	const { t } = useTranslation();
	const history = useHistory();

	return (
		<ListItem
			icon={
				<Status $completed={props.completed}>
					{props.completed ? <CheckmarkIcon size={14} /> : props.index + 1}
				</Status>
			}
			title={
				<Title $completed={props.completed}>
					{t('checkpoints.step')} {props.index + 1}. {props.title}
				</Title>
			}
			meta={props.action}
			trailing={
				<Chevron $completed={props.completed}>
					<ChevronRightIcon size={16} />
				</Chevron>
			}
			onClick={() => history.push(props.url)}
		/>
	);
}

export default function Checkpoints(props: { checkpoints: { checkpoint: string; url: string }[] }) {
	const { t } = useTranslation();

	const workspace = useWorkspace();
	const completed = graphql.useCheckpointsQuery({
		variables: {
			workspaceID: workspace.id,
		},
	});

	if (completed.loading || !completed.data) {
		return null;
	}

	const isCompleted = (key: string) =>
		Boolean(
			completed.data?.workspace.checkpoints?.some(
				(checkpoint) => checkpoint?.key === key && checkpoint?.completedAt,
			),
		);

	const remaining = props.checkpoints.filter((item) => !isCompleted(item.checkpoint)).length;

	if (remaining === 0) {
		return null;
	}

	return (
		<>
			<SectionHeader>
				<Subheading>{t('checkpoints.title')}</Subheading>
				{remaining > 0 && <Count count={remaining} />}
			</SectionHeader>
			<Card p={0}>
				<List>
					{props.checkpoints.map((item, index) => (
						<Checkpoint
							{...item}
							key={item.checkpoint}
							index={index}
							title={t(`checkpoints.${item.checkpoint}.title`)}
							action={t(`checkpoints.${item.checkpoint}.action`)}
							completed={isCompleted(item.checkpoint)}
						/>
					))}
				</List>
			</Card>
		</>
	);
}
